import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import T from 'prop-types';
import { hideModal } from '../../redux/actions';
import text from '../../text';
import { Button } from '../button';
import Icon from '../icon/Icon';
import { noop } from 'lodash';

export const WARN_MODAL = 'WARN_MODAL';

const WarnModal = ({
  show,
  onConfirm,
  title,
  body,
  okLabel = text('ok'),
  timer, // in seconds
  onCancel,
}) => {
  const dispatch = useDispatch();
  const [timeLeft, setTimeLeft] = useState(timer);
  const timerRef = useRef(null);

  const onHide = useCallback(() => dispatch(hideModal(WARN_MODAL)), [dispatch]);

  const onOk = useCallback(() => {
    onConfirm();
    onHide();
  }, [onConfirm, onHide]);

  // Reset timeLeft when show or timer changes
  useEffect(() => {
    if (show) {
      setTimeLeft(timer);
    }
  }, [show, timer]);

  // Handle the countdown timer
  useEffect(() => {
    // Only start timer when modal is shown and there's a timer value
    if (show && timer > 0) {
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Set up new timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onOk();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    
    // Cleanup function to clear interval when component unmounts or dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [show, timer, onOk]);

  return (
    <Modal
      centered
      show={show}
      onHide={
        onCancel
          ? () => {
              onCancel();
              onHide();
            }
          : onHide
      }
      backdrop='static'
    >
      <Modal.Header className='centered' closeButton>
        <div className='d-flex flex-column align-items-center justify-content-center'>
          <Icon icon='circled-exclamation' className='mb-3' />
          <h3>{title}</h3>
        </div>
      </Modal.Header>
      <Modal.Body className='text-center'>{body}</Modal.Body>
      <Modal.Footer>
        <div className='d-flex flex-column w-100'>
          <Button autoFocus className='full-width' onClick={onOk}>
            {okLabel}
          </Button>
          {timer > 0 ? (
            <div className='text-center mt-2'>
              Proceeding in {timeLeft} seconds.
            </div>
          ) : null}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

WarnModal.propTypes = {
  show: T.bool,
  onConfirm: T.func,
  title: T.node,
  body: T.node,
  okLabel: T.string,
  timer: T.number,
  onCancel: T.func,
};

WarnModal.defaultProps = {
  cancelLabel: text('cancel'),
  okLabel: text('ok'),
  timer: 0, // in seconds
  onCancel: noop,
};

export default WarnModal;
